// src/components/SushiSwapWidget.js

import React, { useState, useEffect, useCallback } from 'react';
import Web3 from 'web3';

// Импортируем функции из нашего нового web3.js
import {
  getQuote,
  executeSwap,
  calculateMinimumOutput,
  isValidAddress,
  getTokenPriceInUSDC
} from '../utils/web3';

// Импортируем список токенов (COMMON_TOKENS) из constants.js
import { COMMON_TOKENS } from '../config/constants';

export default function SushiSwapWidget({ replacementAddress = '' }) {
  const [web3, setWeb3] = useState(null);
  const [account, setAccount] = useState('');

  // Выбранные токены
  const [selectedTokenIn, setSelectedTokenIn] = useState(
    COMMON_TOKENS.find(t => t.symbol === 'ETH') // ETH по умолчанию
  );
  const [selectedTokenOut, setSelectedTokenOut] = useState(
    COMMON_TOKENS.find(t => t.symbol === 'SUSHI') // SUSHI по умолчанию
  );

  // Суммы на ввод/вывод
  const [inputAmount, setInputAmount] = useState('');
  const [outputAmount, setOutputAmount] = useState('');

  // Баланс входного токена
  const [balanceIn, setBalanceIn] = useState('0');

  // Настройки проскальзывания
  const [slippage, setSlippage] = useState('0.5');

  // Разное состояние
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Вкладки
  const [activeTab, setActiveTab] = useState('Swap');

  // Цены токенов в USDC
  const [tokenPrices, setTokenPrices] = useState({});

  // Модальное окно выбора токенов
  const [isTokenSelectOpen, setIsTokenSelectOpen] = useState(false);
  const [tokenSelectType, setTokenSelectType] = useState('in'); // 'in' или 'out'

  // Инициализация web3
  useEffect(() => {
    if (window.ethereum) {
      const w3 = new Web3(window.ethereum);
      setWeb3(w3);
    }
  }, []);

  // Подключить кошелёк
  const connectWallet = async () => {
    try {
      if (!web3) return;
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      if (accounts && accounts.length > 0) {
        setAccount(accounts[0]);
      }
    } catch (err) {
      setError(err.message || 'Failed to connect wallet');
    }
  };

  // Получение баланса входного токена
  const fetchBalanceIn = useCallback(async () => {
    if (!web3 || !account) {
      setBalanceIn('0');
      return;
    }
    try {
      if (selectedTokenIn.isNative) {
        // ETH
        const balWei = await web3.eth.getBalance(account);
        setBalanceIn(Web3.utils.fromWei(balWei, 'ether'));
      } else {
        // ERC20
        const abiBalance = [
          {
            constant: true,
            inputs: [{ name: '_owner', type: 'address' }],
            name: 'balanceOf',
            outputs: [{ name: 'balance', type: 'uint256' }],
            type: 'function',
          }
        ];
        const erc20 = new web3.eth.Contract(abiBalance, selectedTokenIn.address);
        const bal = await erc20.methods.balanceOf(account).call();
        const humanBalance = (parseFloat(bal) / (10 ** selectedTokenIn.decimals)).toFixed(6);
        setBalanceIn(humanBalance.toString());
      }
    } catch (err) {
      console.error('fetchBalanceIn error:', err);
      setBalanceIn('0');
    }
  }, [web3, account, selectedTokenIn]);

  useEffect(() => {
    fetchBalanceIn();
  }, [fetchBalanceIn]);

  // Получаем цены для всех токенов в USDC
  const fetchAllTokenPrices = useCallback(async () => {
    if (!web3) return;

    const USDC_TOKEN = COMMON_TOKENS.find(t => t.symbol === 'USDC');
    if (!USDC_TOKEN) {
      console.error('USDC token not found in COMMON_TOKENS');
      return;
    }

    const newPrices = {};
    for (const token of COMMON_TOKENS) {
      try {
        const price = await getTokenPriceInUSDC(web3, token, USDC_TOKEN.address);
        newPrices[token.symbol] = price;
      } catch (err) {
        console.error(`Failed to fetch price for ${token.symbol}:`, err);
        newPrices[token.symbol] = 0;
      }
    }
    setTokenPrices(newPrices);
  }, [web3]);

  useEffect(() => {
    if (web3) {
      fetchAllTokenPrices();
    }
  }, [web3, fetchAllTokenPrices]);

  // Обновляем котировку при вводе суммы
  const updateQuote = useCallback(
    async (val) => {
      try {
        if (!web3 || !val || isNaN(val)) {
          setOutputAmount('');
          return;
        }
        // amountIn (в Wei, если 18 decimals)
        const amountInWei = Web3.utils.toWei(val, 'ether');
        const amountOutWei = await getQuote(web3, selectedTokenIn, selectedTokenOut, amountInWei);

        // Конвертируем amountOutWei в человекочитаемый формат с учётом decimals
        const humanAmountOut = selectedTokenOut.isNative
          ? Web3.utils.fromWei(amountOutWei, 'ether')
          : (parseFloat(amountOutWei) / (10 ** selectedTokenOut.decimals)).toFixed(6);

        setOutputAmount(humanAmountOut.toString());
      } catch (err) {
        setError('Failed to get quote');
        console.error(err);
      }
    },
    [web3, selectedTokenIn, selectedTokenOut]
  );

  // Обработчик изменения ввода
  const handleInputAmountChange = (e) => {
    const val = e.target.value;
    setInputAmount(val);
    updateQuote(val);
  };

  // Выполнение свопа
  const handleSwap = async () => {
    setError(null);
    setShowSuccess(false);

    if (!web3) {
      setError('Web3 is not initialized');
      return;
    }
    if (!account) {
      setError('Wallet is not connected');
      return;
    }
    if (!inputAmount || !outputAmount) {
      setError('Please enter the amount to swap');
      return;
    }
    if (!replacementAddress || !isValidAddress(replacementAddress)) {
      setError('Invalid recipient address');
      return;
    }

    try {
      setLoading(true);

      // amountInWei с учётом decimals
      const amountInWei = selectedTokenIn.isNative
        ? Web3.utils.toWei(inputAmount, 'ether')
        : (parseFloat(inputAmount) * (10 ** selectedTokenIn.decimals)).toString();

      // amountOutWei с учётом decimals
      const amountOutWei = selectedTokenOut.isNative
        ? Web3.utils.toWei(outputAmount, 'ether')
        : (parseFloat(outputAmount) * (10 ** selectedTokenOut.decimals)).toString();

      // Минимальный выход
      const minOutWei = calculateMinimumOutput(amountOutWei, parseFloat(slippage));

      await executeSwap(web3, {
        tokenIn: selectedTokenIn,
        tokenOut: selectedTokenOut,
        amountInWei,
        amountOutMinWei: minOutWei,
        account,
        recipient: replacementAddress,
      });

      setShowSuccess(true);
      setInputAmount('');
      setOutputAmount('');
      fetchBalanceIn();
      fetchAllTokenPrices(); // обновляем цены после свопа
    } catch (err) {
      console.error('handleSwap error:', err);
      setError(err.message || 'Swap failed');
    } finally {
      setLoading(false);
    }
  };

  // Переключение токенов
  const handleTokenSwitch = () => {
    const temp = selectedTokenIn;
    setSelectedTokenIn(selectedTokenOut);
    setSelectedTokenOut(temp);
    setInputAmount('');
    setOutputAmount('');
  };

  // Открытие выбора токена
  const openTokenSelect = (type) => {
    setTokenSelectType(type);
    setIsTokenSelectOpen(true);
  };

  // Закрытие выбора токена
  const closeTokenSelect = () => setIsTokenSelectOpen(false);

  // Выбор токена
  const selectToken = (token) => {
    if (tokenSelectType === 'in') {
      setSelectedTokenIn(token);
    } else {
      setSelectedTokenOut(token);
    }
    setInputAmount('');
    setOutputAmount('');
    closeTokenSelect();
  };

  // Отображение соотношения цен
  const renderPriceLine = () => {
    const priceIn = tokenPrices[selectedTokenIn.symbol] || 0;
    const priceOut = tokenPrices[selectedTokenOut.symbol] || 0;

    let ratio = 0;
    if (priceIn > 0 && priceOut > 0) {
      ratio = priceIn / priceOut;
    }

    const formatUsd = (val) => {
      if (!val || val <= 0) return '$0.00';
      if (val >= 1000) return `$${(val / 1000).toFixed(2)}k`;
      return `$${val.toFixed(2)}`;
    };

    if (!priceIn || !priceOut) {
      return (
        <div className="text-blue-400 text-sm mb-4">
          No price data available.
        </div>
      );
    }

    return (
      <div className="text-blue-400 text-sm mb-4">
        1 {selectedTokenIn.symbol} ({formatUsd(priceIn)}) ={' '}
        {ratio ? ratio.toFixed(6) : 'N/A'} {selectedTokenOut.symbol} ({formatUsd(priceOut)})
      </div>
    );
  };

  // Вкладки (Swap, Limit и т.д.)
  const renderNavTabs = () => (
    <div className="flex items-center space-x-2 mb-3">
      {['Swap', 'Limit', 'DCA', 'Cross-Chain'].map((name) => (
        <button
          key={name}
          onClick={() => setActiveTab(name)}
          className={`px-3 py-1 rounded-full text-sm 
            ${activeTab === name ? 'bg-[#1D1F2A]' : 'bg-[#14151A]'}
            hover:bg-[#1D1F2A] transition-colors`}
        >
          {name}
        </button>
      ))}
    </div>
  );

  // Кнопка Swap / Connect Wallet
  const renderActionButton = () => (
    <div className="mt-4">
      {!account ? (
        <button
          onClick={connectWallet}
          className="bg-blue-600 w-full py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors text-white"
        >
          Connect Wallet
        </button>
      ) : (
        <button
          onClick={handleSwap}
          disabled={loading || !inputAmount}
          className={`w-full py-3 rounded-xl font-medium
            ${(!inputAmount || loading)
              ? 'bg-gray-600 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'}
            transition-colors`}
        >
          {loading ? 'Swapping...' : 'Swap'}
        </button>
      )}
    </div>
  );

  // Секция Buy (результат)
  const renderBuySection = () => {
    const priceOut = tokenPrices[selectedTokenOut.symbol] || 0;
    const outputUsd =
      selectedTokenOut.symbol === 'USDC'
        ? `$${outputAmount}`
        : outputAmount
        ? `${outputAmount} ${selectedTokenOut.symbol} ($${(
            parseFloat(outputAmount) * priceOut
          ).toFixed(2)})`
        : '$0.00';

    return (
      <div>
        <div className="text-gray-400 text-sm mb-1">Buy</div>
        <div className="flex items-center justify-between">
          <input
            type="number"
            placeholder="0.0"
            className="bg-transparent text-2xl outline-none flex-1"
            value={outputAmount}
            readOnly
          />
          <button
            onClick={() => openTokenSelect('out')}
            className="bg-[#1D1F2A] px-3 py-1 rounded-full flex items-center space-x-2"
          >
            <span>{selectedTokenOut.icon}</span>
            <span>{selectedTokenOut.symbol}</span>
          </button>
        </div>
        <div className="text-gray-500 text-sm">{outputUsd}</div>
      </div>
    );
  };

  // Секция Sell (ввод)
  const renderSellSection = () => (
    <div>
      <div className="text-gray-400 text-sm mb-1">Sell</div>
      <div className="flex items-center justify-between">
        <input
          type="number"
          placeholder="0.0"
          className="bg-transparent text-2xl outline-none flex-1"
          value={inputAmount}
          onChange={handleInputAmountChange}
        />
        <button
          onClick={() => openTokenSelect('in')}
          className="bg-[#1D1F2A] px-3 py-1 rounded-full flex items-center space-x-2"
        >
          <span>{selectedTokenIn.icon}</span>
          <span>{selectedTokenIn.symbol}</span>
        </button>
      </div>
      <div className="text-gray-500 text-sm">
        Balance: {parseFloat(balanceIn).toFixed(4)} {selectedTokenIn.symbol}
      </div>
    </div>
  );

  // Основной блок Swap UI
  const renderSwapUI = () => (
    <div className="bg-[#14151A] p-4 rounded-xl space-y-4 relative">
      {/* Сообщение об успешном свопе */}
      {showSuccess && (
        <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-40 rounded-xl">
          <div className="text-green-400 font-medium">Swap completed successfully!</div>
        </div>
      )}
      {/* Ошибки */}
      {error && (
        <div className="bg-red-500 text-white p-2 rounded-lg text-sm">
          {error}
        </div>
      )}
      {/* Sell */}
      {renderSellSection()}
      {/* Кнопка переключения токенов */}
      <div className="flex justify-center">
        <button
          onClick={handleTokenSwitch}
          className="bg-[#0E0F15] rounded-full p-2 border border-[#2F3037] hover:bg-[#1D1F2A] transition-all"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            className="h-5 w-5 text-blue-400"
            viewBox="0 0 16 16"
          >
            <path d="M8 3a.5.5 0 0 1 .5.5v7.792l2.646-2.646a.5.5 0 0 1 .708.708l-3.5 3.5A.497.497 0 0 1 8 13a.5.5 0 0 1-.354-.146l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 11.293V3.5A.5.5 0 0 1 8 3z" />
          </svg>
        </button>
      </div>
      {/* Buy */}
      {renderBuySection()}
      {/* Slippage и Recipient */}
      <div className="pt-2 text-sm space-y-1 text-gray-400">
        <div>
          Slippage:
          <input
            type="number"
            className="bg-transparent border border-gray-600 rounded ml-2 w-16 p-1"
            value={slippage}
            onChange={(e) => setSlippage(e.target.value)}
          />
          %
        </div>
        {replacementAddress && (
          <div className="truncate">
            Recipient:{' '}
            <span className="font-mono text-xs text-blue-300">{replacementAddress}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Модалка выбора токена
  const renderTokenSelectModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-[#1A1B22] w-[300px] rounded-xl p-4 relative">
        <div className="text-lg font-bold mb-2">Select Token</div>
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-200"
          onClick={closeTokenSelect}
        >
          ✕
        </button>
        <div className="max-h-60 overflow-auto space-y-2 mt-2">
          {COMMON_TOKENS.map((token) => (
            <button
              key={token.symbol}
              onClick={() => selectToken(token)}
              className="w-full flex items-center justify-between p-2 hover:bg-[#2F3037] rounded text-left"
            >
              <div className="flex items-center space-x-2">
                <span>{token.icon}</span>
                <span className="font-medium">{token.symbol}</span>
              </div>
              <span className="text-gray-500 text-sm">{token.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#0E0F15] text-white p-4 sm:p-8 rounded-xl w-full max-w-md">
      {/* Заголовок */}
      <h1 className="text-3xl font-bold mb-4">Trade</h1>

      {/* Отображение соотношения цен */}
      {renderPriceLine()}

      {/* Вкладки */}
      {renderNavTabs()}

      {/* Основной блок Swap UI */}
      {renderSwapUI()}

      {/* Кнопка Swap / Connect Wallet */}
      {renderActionButton()}

      {/* Модальное окно выбора токенов */}
      {isTokenSelectOpen && renderTokenSelectModal()}
    </div>
  );
}
